'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const faqs = [
  {
    q: 'How fast is the pairing process?',
    a: 'Average match time is 47 seconds. Our system instantly scans available Genies based on your specific need, expertise level, and time zone. During peak hours (9am–6pm EST), matches happen even faster — often under 20 seconds.',
  },
  {
    q: 'How does screen sharing work inside Summons?',
    a: 'Screen sharing is built directly into the Summons app — no Zoom, Google Meet, or third-party tool needed. Once your session starts, either party can initiate screen sharing with one tap. It works on both iOS and Android, and on the web app.',
  },
  {
    q: 'How does escrow protect me as a client?',
    a: 'When you book a session, your payment is held in secure escrow — not paid to the Genie yet. You review the work at the end of the session. Only when you tap "Approve" does the payment release. If you\'re not satisfied, you can request revisions or open a dispute.',
  },
  {
    q: 'Can agencies use Summons for client work?',
    a: 'Yes. Summons has an Agency tier that supports team accounts, white-label sessions, multi-client management, and bulk booking. Agencies can book Genies on behalf of clients and maintain full billing separation. Contact us for agency pricing.',
  },
  {
    q: 'Can Genies (experts) earn instantly?',
    a: 'Yes — this is one of Summons\'s core promises. The moment a client approves a session, the payout is transferred instantly to the Genie\'s account. No 7-day holds, no 30-day payment cycles. Genies are paid for their time immediately.',
  },
  {
    q: 'What happens if there\'s a dispute?',
    a: 'Every session is fully documented including chat logs, screen share recordings, and file transfers. If a dispute arises, our Trust & Safety team reviews the session record and mediates fairly. We have a 94% dispute resolution satisfaction rate.',
  },
  {
    q: 'What types of marketing experts are available?',
    a: 'Summons has Genies across 40+ marketing specializations including Paid Ads (Google, Meta, TikTok), SEO, Email Marketing, Funnel Building, Copywriting, Branding, Web Development, Analytics, Creative Direction, Influencer Strategy, and more.',
  },
  {
    q: 'Is there a subscription or monthly fee?',
    a: 'No subscriptions. Summons is completely pay-per-session. You download the app for free, describe your need, and pay only when you book a Genie. No platform fees, no hidden charges. Genies keep 85% of every session.',
  },
  {
    q: 'How is Summons different from Upwork or Fiverr?',
    a: 'Upwork and Fiverr require you to post a job, wait for proposals, compare freelancers, negotiate, and onboard. That takes days. Summons gets you working with an expert in under 60 seconds. No proposals, no negotiation, no waiting — just instant access to live help.',
  },
  {
    q: 'What if I need a longer engagement, not just one session?',
    a: 'You can book recurring sessions with the same Genie, or establish a retainer relationship directly through the app. Many Summons users have ongoing relationships with their favorite Genies for consistent work — while still having access to new Genies for specialized one-off needs.',
  },
  {
    q: 'Is my business data safe during screen sharing?',
    a: 'All sessions are encrypted end-to-end. Screen sharing streams are never stored unless you explicitly enable session recording. Genies sign a platform NDA by default, and Enterprise clients can add custom NDAs before sessions begin.',
  },
  {
    q: 'How do I become a Genie?',
    a: 'Apply through the Summons app or at app.getsummons.com/become-a-genie. We review your expertise, run a skills assessment, and onboard qualified experts within 48 hours. We\'re selective — only the top 15% of applicants are accepted.',
  },
];

export default function FAQSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'faq_expand', { question: faqs[index].q });
    }
  };

  // FAQ Schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <section id="faq" ref={sectionRef} className="py-20 lg:py-28 px-4 sm:px-6 bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 reveal">
          <p className="text-xs font-bold uppercase tracking-widest text-muted mb-3">Got Questions?</p>
          <h2 className="text-4xl sm:text-5xl font-black text-foreground tracking-tight mb-4">
            Everything You Need
            <br />
            <span className="text-primary">to Know.</span>
          </h2>
          <p className="text-muted text-lg">
            Still have questions? Email us at{' '}
            <a href="mailto:hello@getsummons.com" className="text-accent font-semibold hover:underline">
              hello@getsummons.com
            </a>
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="reveal-scale faq-item bg-surface rounded-2xl border border-border overflow-hidden"
              style={{ transitionDelay: `${Math.min(i * 0.04, 0.3)}s` }}
            >
              <button
                onClick={() => handleToggle(i)}
                className="w-full flex items-center justify-between p-5 sm:p-6 text-left group"
                aria-expanded={openIndex === i}
              >
                <span className="text-sm sm:text-base font-semibold text-foreground pr-4 leading-snug">
                  {faq.q}
                </span>
                <div
                  className={`faq-chevron flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    openIndex === i
                      ? 'bg-primary text-white' :'bg-white border border-border text-muted group-hover:border-primary/30'
                  } ${openIndex === i ? 'open' : ''}`}
                >
                  <Icon name="ChevronDownIcon" size={16} />
                </div>
              </button>

              <div className={`faq-content ${openIndex === i ? 'open' : ''}`}>
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-0">
                  <div className="h-px bg-border mb-4" />
                  <p className="text-sm text-muted leading-relaxed">{faq.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}